import {
    CurrencyCode,
    findCurrencySymbolByCode,
    formatCurrency,
    formatNumber,
    formatNumberForDimensions,
    getCurrencyFromRegion,
    getDimensionUnit,
    getPricingUnit,
    getProductNameFields,
    getRegion,
    getTradeUnit,
    ListingBuyerModel,
    TranslateFunc,
} from '@steelbuy/ts-shared';
import { getThicknessText, getQualityDisplayValue } from '@steelbuy/util';

type ProductNameFieldMap = Record<string, (listing: ListingBuyerModel, t: TranslateFunc) => string>;

const formatDimensionValue = (value: number, t: TranslateFunc): string =>
    t(`domainModel:material.dimensions.units.${getDimensionUnit()}.labelWithValue`, {
        value: formatNumberForDimensions(value),
    });

const productNameFieldMap: ProductNameFieldMap = {
    definition: (listing, t) =>
        'definition' in listing.material && listing.material.definition
            ? t(`domainModel:material.definition.value.${listing.material.definition}`)
            : '',
    dimensions: (listing, t) => {
        const thickness = getThicknessText(
            listing.materialDimensions.thickness,
            listing.materialDimensions.thicknessType,
            t,
            true
        );
        const parts = [thickness, formatDimensionValue(listing.materialDimensions.width, t)];

        if ('length' in listing.materialDimensions && listing.materialDimensions.length) {
            parts.push(formatDimensionValue(listing.materialDimensions.length, t));
        }

        return parts.join(' x ');
    },
    grade: (listing, t) =>
        'grade' in listing.material && listing.material.grade
            ? t(`domainModel:material.grade.value.${listing.material.grade}`)
            : '',
    product: (listing, t) =>
        'product' in listing.material && listing.material.product
            ? t(`domainModel:material.product.value.${listing.material.product}`)
            : '',
    finish: (listing, t) =>
        'finish' in listing.material && listing.material.finish
            ? t(`domainModel:material.finish.value.${listing.material.finish}`)
            : '',
    temper: (listing, t) =>
        'temper' in listing.material && listing.material.temper
            ? t(`domainModel:material.temper.value.${listing.material.temper}`)
            : '',
};

export const buildProductName = (
    listing: ListingBuyerModel,
    t: TranslateFunc,
    overrides: Partial<ProductNameFieldMap> = {}
): string => {
    const fields = getProductNameFields(listing.material) as string[];
    const fieldMap = { ...productNameFieldMap, ...overrides };

    return fields
        .map((field) => fieldMap[field]?.(listing, t) ?? '')
        .filter(Boolean)
        .join(' ');
};

export const getProductName = (listing: ListingBuyerModel, t: TranslateFunc): string => buildProductName(listing, t);

type ListingTableConfig = Partial<
    Record<
        string,
        {
            getHeading: (
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                formData: Record<string, any>,
                t: TranslateFunc
            ) => string;
            mapValue?: (listing: ListingBuyerModel, t: TranslateFunc) => string | number | undefined;
            getTooltip?: (t: TranslateFunc) => string | number | undefined;
        }
    >
>;

export const listingTableConfig: ListingTableConfig = {
    productName: {
        getHeading: (formData, t) => t('application.dashboardListings.tableHeadings.productName'),
        mapValue: getProductName,
    },
    gauge: {
        getHeading: (formData, t) => t('application.dashboardListings.tableHeadings.gauge'),
        mapValue: (listing) => listing.gauge ?? '',
    },
    quality: {
        getHeading: (formData, t) => t('application.dashboardListings.tableHeadings.quality'),
        mapValue: (listing, t) => getQualityDisplayValue(listing.quality, listing.prime, t),
    },
    location: {
        getHeading: (formData, t) => t('application.dashboardListings.tableHeadings.location'),
        mapValue: (listing, t) => {
            const locationTextArray = [];
            if (listing.state) {
                locationTextArray.push(t(`domainModel:address.shortState.${listing.state}`));
            }
            if (listing.country) {
                locationTextArray.push(t(`domainModel:address.shortCountry.${listing.country}`));
            }
            return locationTextArray.length ? locationTextArray.join(', ') : '';
        },
    },
    weight: {
        getHeading: (formData, t) =>
            t('application.createBulkListing.fields.weight', {
                tradeUnit: t(`domainModel:material.tradeUnit.value.${getTradeUnit(formData)}.pluralLabel`),
            }),
        mapValue: (listing) => formatNumber(listing.weight),
    },
    price: {
        getHeading: (formData, t) =>
            `${findCurrencySymbolByCode(getCurrencyFromRegion(getRegion()) as CurrencyCode)}/${t(
                `domainModel:material.pricingUnit.value.${getPricingUnit(formData)}.label`
            )}`,
        mapValue: (listing: ListingBuyerModel) => {
            if (listing?.pricing?.priceIncludingDelivery) {
                return formatCurrency(
                    listing.pricing.priceIncludingDelivery.value,
                    getCurrencyFromRegion(getRegion()) as CurrencyCode
                );
            }
            return '';
        },
        getTooltip: (t) => t('application.dashboardListings.deliveredTotal'),
    },
};
